// Property listing-requests parser — DTO (wire shape) → view model. The seam
// every consumer must respect. Containers / components / composables never read
// a wire field directly (and never a snake_case owner field — the parser is
// the only place `owners[]` is normalized to camelCase).

import { LISTING_REQUEST_STATUS } from '../constants/listingRequestStatus'
import type {
  CreateDraftPropertyResponseDto,
  DeclaredOwnerOnboardingDto,
  DeclaredOwnerPresenceDto,
  DldVerificationDataDto,
  DldVerificationPerOwnerDto,
  ListingRequestDocumentDownloadDto,
  ListingRequestDto,
  ListingRequestEventDto,
  ListingRequestOwnerDto,
  ListingRequestsListResponseDto,
  ListingRequestStatusCountsDto,
  OwnerInviteStateDto,
  PromoteListingRequestResponseDto,
  PromotionReadinessDto,
  PropertyOnboardingDto,
  ListingRequestFeeQuoteDto,
} from './listingRequestsContracts'
import type {
  CreateDraftPropertyResult,
  DeclaredOwnerOnboarding,
  DeclaredOwnerPresence,
  DldVerificationData,
  DldVerificationPerOwner,
  ListingRequest,
  ListingRequestCapabilities,
  ListingRequestDocumentDownload,
  ListingRequestEvent,
  ListingRequestOwner,
  ListingRequestPromotionReadiness,
  ListingRequestsListResponse,
  ListingRequestStatus,
  ListingRequestStatusCounts,
  OwnerInviteState,
  PromoteListingRequestResult,
  PropertyOnboarding,
  ListingRequestFeeQuote,
} from '../types/listingRequestsTypes'

function parseOwner(dto: ListingRequestOwnerDto): ListingRequestOwner {
  return {
    fullName: String(dto.full_name ?? ''),
    role: dto.role ?? null,
    ownershipPercentage:
      dto.ownership_percentage === undefined || dto.ownership_percentage === null
        ? null
        : Number(dto.ownership_percentage),
    isPrimary: Boolean(dto.is_primary),
    dldVerified: Boolean(dto.dld_verified),
    email: dto.email ?? null,
    mobileNumber: dto.mobile_number ?? null,
    emiratesId: dto.emirates_id ?? null,
    ownerType: dto.owner_type ?? null,
  }
}

function parseDeclaredOwnerPresence(dto: DeclaredOwnerPresenceDto): DeclaredOwnerPresence {
  return {
    index: Number(dto.index),
    isOnPlatform: Boolean(dto.isOnPlatform),
    isLinked: Boolean(dto.isLinked),
    propertyOwnerId: dto.propertyOwnerId ?? null,
    propertyOwnerName: dto.propertyOwnerName ?? null,
    propertyOwnerEmail: dto.propertyOwnerEmail ?? null,
    propertyOwnerStatus: dto.propertyOwnerStatus ?? null,
    matchedBy: dto.matchedBy ?? null,
  }
}

// Null-preserving like `parseOwnersOnboarding` below: a list row carries no
// resolution at all, and an empty array would claim every owner is off-platform.
function parseOwnersPresence(
  dto: DeclaredOwnerPresenceDto[] | null | undefined,
): DeclaredOwnerPresence[] | null {
  if (!dto) return null
  return dto.map(parseDeclaredOwnerPresence)
}

function parsePerOwner(dto: DldVerificationPerOwnerDto): DldVerificationPerOwner {
  return {
    index: Number(dto.index ?? 0),
    verifiedByAdminId: String(dto.verifiedByAdminId ?? ''),
    verifiedAt: String(dto.verifiedAt ?? ''),
    note: dto.note ?? null,
  }
}

function parseDldVerificationData(
  dto: DldVerificationDataDto | null | undefined,
): DldVerificationData | null {
  if (!dto) return null
  return {
    method: String(dto.method ?? ''),
    perOwner: (dto.perOwner ?? []).map(parsePerOwner),
    completedByAdminId: dto.completedByAdminId ?? null,
    completedAt: dto.completedAt ?? null,
  }
}

function parseCapabilities(dto: ListingRequestDto): ListingRequestCapabilities {
  return {
    canApprove: Boolean(dto.canApprove),
    canReject: Boolean(dto.canReject),
    canRequestRevision: Boolean(dto.canRequestRevision),
    canProposePrice: Boolean(dto.canProposePrice),
    canVerifyOwners: Boolean(dto.canVerifyOwners),
    canStartOwnerOnboarding: Boolean(dto.canStartOwnerOnboarding),
    canCreateDraftProperty: Boolean(dto.canCreateDraftProperty),
    canPromote: Boolean(dto.canPromote),
  }
}

// Null (not a zeroed object) outside the onboarding window — same meaningful
// absence `ownersOnboarding` carries. A default-shaped readiness would read as
// "nothing is missing, market unresolved, ownership invalid" on every list row.
function parsePromotionReadiness(
  dto: PromotionReadinessDto | null | undefined,
): ListingRequestPromotionReadiness | null {
  if (!dto) return null
  return {
    missingFields: (dto.missingFields ?? []).map(String),
    isMarketResolved: Boolean(dto.isMarketResolved),
    countryName: dto.countryName ?? null,
    isOwnershipValid: Boolean(dto.isOwnershipValid),
    ownershipTotalPercentage: Number(dto.ownershipTotalPercentage ?? 0),
    primaryOwnerCount: Number(dto.primaryOwnerCount ?? 0),
  }
}

// Null, never a zeroed object: "no property yet" is the state the panel renders a
// CREATE button for, and a default-shaped object would read as an existing property
// with nothing done to it — the one state that must not be confused with the other.
function parsePropertyOnboarding(
  dto: PropertyOnboardingDto | null | undefined,
): PropertyOnboarding | null {
  if (!dto) return null
  return {
    propertyId: String(dto.propertyId ?? ''),
    propertyRefCode: dto.propertyRefCode ?? null,
    propertyName: dto.propertyName ?? null,
    propertyStatus: dto.propertyStatus ?? null,
    opsEnrichmentComplete: Boolean(dto.opsEnrichmentComplete),
    financialsComplete: Boolean(dto.financialsComplete),
  }
}

// Null (not a synthesised empty invite) when the server sends none: "no live
// link" is a state the checklist renders differently from "a link that expired".
function parseOwnerInvite(dto: OwnerInviteStateDto | null | undefined): OwnerInviteState | null {
  if (!dto) return null
  return {
    expiresAt: String(dto.expiresAt ?? ''),
    isExpired: Boolean(dto.isExpired),
    sentAt: dto.sentAt ?? null,
    firstResolvedAt: dto.firstResolvedAt ?? null,
    lastResolvedAt: dto.lastResolvedAt ?? null,
  }
}

function parseDeclaredOwnerOnboarding(dto: DeclaredOwnerOnboardingDto): DeclaredOwnerOnboarding {
  return {
    index: Number(dto.index ?? 0),
    fullName: String(dto.fullName ?? ''),
    email: dto.email ?? null,
    mobileNumber: dto.mobileNumber ?? null,
    ownershipPercentage:
      dto.ownershipPercentage === undefined || dto.ownershipPercentage === null
        ? null
        : Number(dto.ownershipPercentage),
    isPrimary: Boolean(dto.isPrimary),
    outcome: dto.outcome,
    linkSource: dto.linkSource ?? null,
    propertyOwnerId: dto.propertyOwnerId ?? null,
    propertyOwnerName: dto.propertyOwnerName ?? null,
    propertyOwnerEmail: dto.propertyOwnerEmail ?? null,
    propertyOwnerStatus: dto.propertyOwnerStatus ?? null,
    invite: parseOwnerInvite(dto.invite),
  }
}

// Null (not []) outside the onboarding window — the absence is meaningful: it
// says "this request isn't in the stage", not "it has no owners".
function parseOwnersOnboarding(
  dto: DeclaredOwnerOnboardingDto[] | null | undefined,
): DeclaredOwnerOnboarding[] | null {
  if (!dto) return null
  return dto.map(parseDeclaredOwnerOnboarding)
}

export function parseListingRequest(dto: ListingRequestDto): ListingRequest {
  return {
    id: String(dto.id ?? ''),
    requestRefCode: dto.requestRefCode ?? null,
    sellerId: String(dto.sellerId ?? ''),
    requestStatus: dto.requestStatus,
    stage: dto.stage,
    stageStep: dto.stageStep,
    stageTotal: dto.stageTotal,
    stageState: dto.stageState,
    priceNegotiationStatus: dto.priceNegotiationStatus,
    ownershipRole: dto.ownershipRole ?? null,
    propertyType: dto.propertyType ?? null,
    owners: (dto.owners ?? []).map(parseOwner),
    certificateNumber: dto.certificateNumber ?? null,
    plotNumber: dto.plotNumber ?? null,
    propertyNumber: dto.propertyNumber ?? null,
    buildingName: dto.buildingName ?? null,
    locationArea: dto.locationArea ?? null,
    addressLine1: dto.addressLine1 ?? null,
    city: dto.city ?? null,
    countryId: dto.countryId ?? null,
    municipalityNumber: dto.municipalityNumber ?? null,
    totalAreaSqm: dto.totalAreaSqm ?? null,
    titleDeedFilePath: dto.titleDeedFilePath ?? null,
    titleDeedFileName: dto.titleDeedFileName ?? null,
    isRented: dto.isRented ?? false,
    isFreehold: dto.isFreehold ?? false,
    isResidential: dto.isResidential ?? false,
    isMortgageFree: dto.isMortgageFree ?? false,
    isDldBlockFree: dto.isDldBlockFree ?? false,
    termsAcceptedAt: dto.termsAcceptedAt ?? null,
    rentalAgreementFilePath: dto.rentalAgreementFilePath ?? null,
    rentalAgreementFileName: dto.rentalAgreementFileName ?? null,
    tenantName: dto.tenantName ?? null,
    annualRentAmount: dto.annualRentAmount ?? null,
    monthlyRentAmount: dto.monthlyRentAmount ?? null,
    leaseStart: dto.leaseStart ?? null,
    leaseEnd: dto.leaseEnd ?? null,
    propertyName: dto.propertyName ?? null,
    askingPrice: dto.askingPrice ?? null,
    adminProposedPrice: dto.adminProposedPrice ?? null,
    priceAgreed: dto.priceAgreed ?? null,
    currencyCode: String(dto.currencyCode ?? ''),
    valuationMin: dto.valuationMin ?? null,
    valuationMax: dto.valuationMax ?? null,
    sellerNotes: dto.sellerNotes ?? null,
    decisionNotes: dto.decisionNotes ?? null,
    rejectionReason: dto.rejectionReason ?? null,
    revisionNotes: dto.revisionNotes ?? null,
    submittedAt: dto.submittedAt ?? null,
    reviewedAt: dto.reviewedAt ?? null,
    approvedAt: dto.approvedAt ?? null,
    agreementCompletedAt: dto.agreementCompletedAt ?? null,
    fundingDeadlineAt: dto.fundingDeadlineAt ?? null,
    fundingDaysRemaining: dto.fundingDaysRemaining ?? null,
    isFundingWindowLapsed: Boolean(dto.isFundingWindowLapsed),
    fundingWindowDays: dto.fundingWindowDays ?? null,
    canSetFundingWindow: Boolean(dto.canSetFundingWindow),
    ownersVerifiedAt: dto.ownersVerifiedAt ?? null,
    reviewedBy: dto.reviewedBy ?? null,
    dldVerificationData: parseDldVerificationData(dto.dldVerificationData),
    createdAt: String(dto.createdAt ?? ''),
    updatedAt: String(dto.updatedAt ?? ''),
    capabilities: parseCapabilities(dto),
    ownersPresence: parseOwnersPresence(dto.ownersPresence),
    ownersOnboarding: parseOwnersOnboarding(dto.ownersOnboarding),
    isOwnersOnboardingComplete: Boolean(dto.isOwnersOnboardingComplete),
    promotionReadiness: parsePromotionReadiness(dto.promotionReadiness),
    propertyOnboarding: parsePropertyOnboarding(dto.propertyOnboarding),
    promotedPropertyId: dto.promotedPropertyId ?? null,
    promotedAt: dto.promotedAt ?? null,
    availableTransitions: dto.availableTransitions ?? [],
  }
}

export function parsePromoteListingRequestResponse(
  dto: PromoteListingRequestResponseDto,
): PromoteListingRequestResult {
  return { propertyId: String(dto.propertyId ?? '') }
}

export function parseCreateDraftPropertyResponse(
  dto: CreateDraftPropertyResponseDto,
): CreateDraftPropertyResult {
  return {
    propertyId: String(dto.propertyId ?? ''),
    wasCreated: Boolean(dto.wasCreated),
    hasTitleDeed: Boolean(dto.hasTitleDeed),
    hasRentalAgreement: Boolean(dto.hasRentalAgreement),
  }
}

export function parseListingRequestStatusCounts(
  dto: ListingRequestStatusCountsDto,
): ListingRequestStatusCounts {
  const byStatus: Record<ListingRequestStatus, number> = {
    [LISTING_REQUEST_STATUS.DRAFT]: Number(dto.draft ?? 0),
    [LISTING_REQUEST_STATUS.SUBMITTED]: Number(dto.submitted ?? 0),
    [LISTING_REQUEST_STATUS.UNDER_REVIEW]: Number(dto.underReview ?? 0),
    [LISTING_REQUEST_STATUS.REVISION_REQUESTED]: Number(dto.revisionRequested ?? 0),
    [LISTING_REQUEST_STATUS.REJECTED]: Number(dto.rejected ?? 0),
    [LISTING_REQUEST_STATUS.APPROVED]: Number(dto.approved ?? 0),
    [LISTING_REQUEST_STATUS.OWNER_VERIFICATION]: Number(dto.ownerVerification ?? 0),
    [LISTING_REQUEST_STATUS.AGREEMENT_IN_PROGRESS]: Number(dto.agreementInProgress ?? 0),
    [LISTING_REQUEST_STATUS.AGREEMENT_REJECTED]: Number(dto.agreementRejected ?? 0),
    [LISTING_REQUEST_STATUS.AGREEMENT_COMPLETED]: Number(dto.agreementCompleted ?? 0),
    [LISTING_REQUEST_STATUS.OWNERS_ONBOARDING]: Number(dto.ownersOnboarding ?? 0),
    [LISTING_REQUEST_STATUS.PROMOTED]: Number(dto.promoted ?? 0),
    [LISTING_REQUEST_STATUS.WITHDRAWN]: Number(dto.withdrawn ?? 0),
  }
  return { all: Number(dto.all ?? 0), byStatus }
}

export function parseListingRequestsListResponse(
  dto: ListingRequestsListResponseDto,
): ListingRequestsListResponse {
  const limit = Number(dto.meta?.limit ?? 0)
  const total = Number(dto.meta?.total ?? 0)
  const page = Number(dto.meta?.page ?? 1)
  const totalPages =
    dto.meta?.totalPages !== undefined && dto.meta?.totalPages !== null
      ? Number(dto.meta.totalPages)
      : limit > 0
        ? Math.ceil(total / limit)
        : 0
  return {
    data: (dto.rows ?? []).map(parseListingRequest),
    meta: { total, page, limit, totalPages },
  }
}

function parseListingRequestEvent(dto: ListingRequestEventDto): ListingRequestEvent {
  return {
    id: String(dto.id ?? ''),
    eventType: String(dto.eventType ?? ''),
    actorType: String(dto.actorType ?? ''),
    summary: dto.summary ?? null,
    data: dto.data ?? null,
    createdAt: String(dto.createdAt ?? ''),
  }
}

export function parseListingRequestTimeline(
  dtos: ListingRequestEventDto[] | null | undefined,
): ListingRequestEvent[] {
  return (dtos ?? []).map(parseListingRequestEvent)
}

/**
 * Signed-download response → view model.
 *
 * `url` is the one field with no sensible empty default: a blank string would sail
 * through to `window.open('')`, which opens a dead tab instead of surfacing that
 * the server sent nothing usable. Guard at the seam rather than in the caller.
 */
export function parseListingRequestDocumentDownload(
  dto: ListingRequestDocumentDownloadDto | null | undefined,
): ListingRequestDocumentDownload {
  const url = dto?.url ?? ''
  if (!url) throw new Error('The download link was missing from the server response.')
  return {
    url,
    expiresAt: dto?.expiresAt ?? '',
    fileName: dto?.fileName ?? '',
  }
}

/**
 * The quote is dropped to what the price panel renders. The definition columns
 * the service also sends (`paidToRole`, the per-fee VAT split) describe how each
 * figure was arrived at and have no place on a four-row side card — a reviewer
 * is told what the seller nets, not how the fee model is shaped.
 */
export function parseListingRequestFeeQuote(
  dto: ListingRequestFeeQuoteDto,
): ListingRequestFeeQuote {
  return {
    quotedPrice: dto.quotedPrice,
    isPriceAgreed: dto.isPriceAgreed,
    lines: dto.lines.map((line) => ({
      code: line.code,
      label: line.label,
      rateLabel: line.rateLabel ?? null,
      totalAmount: line.totalAmount,
    })),
    totalDeductionAmount: dto.totalDeductionAmount,
    netSettlementAmount: dto.netSettlementAmount,
    isNetNegative: dto.isNetNegative,
  }
}
